import { SelectChangeEvent } from "@mui/material";
import { createContext, FC, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { deleteSuscriptionById, getAllOrganizations, getAllPlans, getAllSuscriptions, postSuscription, putSuscriptionById } from "../../api/broker";
import { useAuthContext } from "../../hooks/Auth";
import { SuscriptsContextProps } from "../../interfaces/SuperAdmin/ContextProps/iSuscriptions";
import { iDataOrganizacion } from "../../interfaces/SuperAdmin/Pages/iDataOrganizacion";
import { iDataPlan } from "../../interfaces/SuperAdmin/Pages/iDataPlan";
import {
  iDataSuscripcionOrg,
  PostSuscriptFields,
  PutSuscriptFields,
} from "../../interfaces/SuperAdmin/Pages/iDataSuscripcion";

const SuscriptsContext = createContext<SuscriptsContextProps>(
  {} as SuscriptsContextProps
);

interface Props {
  children: React.ReactNode;
}

const SuscriptsProvider: FC<Props> = ({ children }) => {
  const { idOrganization } = useParams();
  const { canAccess } = useAuthContext();
  const navigate = useNavigate();

  const [organizaciones, setOrganizaciones] = useState<iDataOrganizacion[]>([]);

  const [selectedOrg, setSelectedOrg] = useState<string | undefined>(
    idOrganization
  );
  const [selectedSuscript, setSelectedSuscript] =
    useState<iDataSuscripcionOrg | null>(null);

  const [suscriptions, setSuscriptions] = useState<iDataSuscripcionOrg[]>([]);
  const [planes, setPlanes] = useState<iDataPlan[]>([]);
  const [searchText, setSearchText] = useState("");

  const [isLoadingSuscriptions, setIsLoadingSuscriptions] = useState(true);
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);

  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [toDeleteSuscr, setToDeleteSuscr] = useState("");

  // Función para extraer todas las organizaciones
  const fetchOrgs = async () => {
    setIsLoadingOrgs(true);
    try {
      const orgRes = await getAllOrganizations();

      if (orgRes.data.statusCode === 200) {
        setOrganizaciones(orgRes.data.body);
      } else {
        toast.error("Error al obtener las organizaciones");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al obtener las organizaciones");
    } finally {
      setIsLoadingOrgs(false);
    }
  };

  useEffect(() => {
    const isSuperAdmin = canAccess("view:suscriptions");
    if (isSuperAdmin) fetchOrgs();
  }, []);

  // Función para extraer planes
  const fetchPlans = async (estado: string) => {
    setIsLoadingPlans(true);
    try {
      const planRes = await getAllPlans(estado, selectedOrg);

      if (planRes.data.statusCode === 200) {
        setPlanes(planRes.data.body);
      } else {
        toast.error("Error al obtener los planes");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al obtener los planes");
    } finally {
      setIsLoadingPlans(false);
    }
  };

  useEffect(() => {
    const fetchSuscriptions = async (id: string) => {
      setIsLoadingSuscriptions(true);
      try {
        const res = await getAllSuscriptions(id);
        if (res.data.statusCode === 200) {
          console.log(res);
          setSuscriptions(res.data.body);
        } else {
          toast.error("Error al obtener las suscripciones");
        }
      } catch (error) {
        console.log(error);
        toast.error("Error al obtener las suscripciones");
      } finally {
        setIsLoadingSuscriptions(false);
      }
    };

    if (selectedOrg) {
      fetchSuscriptions(selectedOrg);
    }
  }, [selectedOrg]);

  const handleChange = async (event: SelectChangeEvent) => {
    setSelectedOrg(event.target.value);
    setPlanes([]);
    navigate(`/asistente/suscriptions/${event.target.value}`);
  };

  const handleEdit = (suscription: iDataSuscripcionOrg) => {
    setSelectedSuscript(suscription);
    toggleEditModal(true);
  };

  const assignToDeleteSuscr = (suscrId: string) => {
    setToDeleteSuscr(suscrId);
  };

  const handleDelete = async () => {
    if (toDeleteSuscr) {
      setIsDeleting(true);
      try {
        const res = await deleteSuscriptionById(toDeleteSuscr);
        console.log(res);

        if (res.data.statusCode === 200) {
          setSuscriptions((prev) => {
            const filteredSuscr = prev.filter(
              (suscr) => suscr.id_suscripcion !== toDeleteSuscr
            );
            return filteredSuscr;
          });
          setToDeleteSuscr("");
          toast.success("Suscripción eliminada correctamente");
        } else {
          toast.error("Error al eliminar la suscripción");
        }
      } catch (error) {
        console.log(error);
        toast.error("Error al eliminar la suscripción");
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const toggleCreateModal = (isOpen: boolean) => {
    if (isOpen) {
      fetchPlans("Activo");
    }
    setIsCreateModalOpen(isOpen);
  };

  const toggleEditModal = (isOpen: boolean) => {
    if (!isOpen) {
      setSelectedSuscript(null);
    }
    setIsEditModalOpen(isOpen);
  };

  const createSuscription = async (data: PostSuscriptFields) => {
    setIsCreating(true);
    try {
      const susRes = await postSuscription(data);
      if (susRes.data.statusCode === 201) {
        setSuscriptions((prev) => [...prev, susRes.data.body]);
        toggleCreateModal(false);
        toast.success("Suscripción creada exitosamente");
      } else {
        toast.error("Error al crear la suscripción");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al crear la suscripción");
    } finally {
      setIsCreating(false);
    }
  };

  const editSuscription = async (
    id_suscripcion: string,
    data: PutSuscriptFields
  ) => {
    setIsEditing(true);
    try {
      const res = await putSuscriptionById(data, id_suscripcion);
      console.log(res);

      if (res.data.statusCode === 200) {
        setSuscriptions((prev) =>
          prev.map((suscr) => {
            if (suscr.id_suscripcion === id_suscripcion) {
              return res.data.body;
            }
            return suscr;
          })
        );
        toggleEditModal(false);
        toast.success("Suscripción editada exitosamente");
      } else {
        toast.error("Error al editar la suscripción");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al editar la suscripción");
    } finally {
      setIsEditing(false);
    }
  };

  return (
    <SuscriptsContext.Provider
      value={{
        organizaciones,
        selectedOrg,
        searchText,
        suscriptions,
        selectedSuscript,
        planes,
        toDeleteSuscr,
        isLoadingOrgs,
        isLoadingPlans,
        isCreateModalOpen,
        isEditModalOpen,
        isCreating,
        isEditing,
        isDeleting,
        isLoadingSuscriptions,
        handleChange,
        toggleCreateModal,
        toggleEditModal,
        createSuscription,
        editSuscription,
        handleEdit,
        handleDelete,
        assignToDeleteSuscr,
        setSearchText,
      }}
    >
      {children}
    </SuscriptsContext.Provider>
  );
};

const useSuperAdminSuscripts = () => useContext(SuscriptsContext);

export { SuscriptsProvider, useSuperAdminSuscripts };

